import React from 'react';
import { Platform, StyleSheet, TouchableOpacity } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

import { useAppColors } from '../../hooks/useAppColors';
import { useAppleSignIn } from '../../hooks/useAuth';
import { getApiError } from '../../utils/formatters';

interface Props {
  /** Optional callback after Apple sign-in succeeds. Receives the sync response. */
  onSuccess?: (response: any) => void;
}

/**
 * "Continue with Apple" button.
 *
 * On iOS, uses expo-apple-authentication's native AppleSignInButton look.
 * On web/Android, renders a styled button that triggers the Firebase popup
 * flow (Apple Sign In is only available on iOS native; on web it works via
 * Firebase's OAuth redirect/popup).
 *
 * The Apple credential is exchanged for a Firebase ID token, which is then
 * sent to the backend's /users/auth/sync endpoint for verification.
 */
export default function AppleSignInButton({ onSuccess }: Props) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const navigation = useNavigation<any>();
  const appleSignIn = useAppleSignIn();

  const handlePress = () => {
    appleSignIn.mutate(undefined, {
      onSuccess: (response) => {
        if (onSuccess) {
          onSuccess(response);
          return;
        }
        const needsCompletion =
          response.is_new_user ||
          !response.user.dob ||
          !response.user.terms_accepted_at;
        if (needsCompletion) {
          navigation.navigate('AuthCallback', { syncResult: response });
        }
      },
    });
  };

  // Apple Sign In is only available on iOS 13+ and web (via Firebase popup).
  // On Android, hide the button entirely.
  if (Platform.OS === 'android') {
    return null;
  }

  return (
    <>
      <TouchableOpacity
        onPress={handlePress}
        disabled={appleSignIn.isPending}
        style={[styles.socialButton, { backgroundColor: colors.card, borderColor: colors.border }]}
        activeOpacity={0.8}
      >
        {appleSignIn.isPending ? (
          <ActivityIndicator size="small" color={colors.text} />
        ) : (
          <>
            <Icon name="apple" size={20} color={colors.text} style={styles.appleLogo} />
            <Text style={[styles.socialButtonText, { color: colors.text }]}>
              {t('auth.continueWithApple')}
            </Text>
          </>
        )}
      </TouchableOpacity>

      {appleSignIn.isError && (
        <Text style={[styles.error, { color: colors.error }]}>
          {getApiError(appleSignIn.error, t('auth.loginFailed'), t)}
        </Text>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  socialButton: {
    flexDirection: 'row',
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
  },
  socialButtonText: {
    fontSize: 16,
    fontWeight: '500',
    marginLeft: 8,
  },
  appleLogo: {
    marginRight: 2,
  },
  error: {
    textAlign: 'center',
    marginTop: 12,
    fontSize: 13,
  },
});
